import { Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useAbsencesQuery } from '../../../types/planung-graphql-client-defs';
import { getAbsenceReasonTranslation } from '../../../utils/absenceReasons';
import { useSubstitutionStore } from '../../Substitutions/SubstitutionProvider';

export type TeacherAbsenceTableType = {
  uuid: string;
  teacherName: string;
  start: string;
  end: string;
  reason: string;
  comment: string;
};

type TeacherAbsencesTableProps = {
  loading: boolean;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
};

export const TeacherAbsencesTable = observer(({ loading, onAdd, onEdit, onDelete }: TeacherAbsencesTableProps) => {
  const { t } = useTranslation();
  const { selectedDay } = useSubstitutionStore();
  const context = useMemorizedCacheTag('ABSENCES');

  const selectedDayStartString = selectedDay.startOf('day').utc().toISOString();
  const selectedDayEndString = selectedDay.endOf('day').utc().toISOString();

  const [{ data }] = useAbsencesQuery({
    variables: {
      where: {
        end_GTE: selectedDayStartString,
        start_LTE: selectedDayEndString,
      },
    },
    context,
    pause: !selectedDay,
  });

  const absences: TeacherAbsenceTableType[] =
    data?.absences.map((a) => {
      return {
        uuid: a.uuid,
        teacherName: a.person?.listName ?? '',
        start: dayjs(a.start).format('DD.MM.YYYY HH:mm'),
        end: dayjs(a.start).isSame(a.end, 'day')
          ? dayjs(a.end).format('HH:mm')
          : dayjs(a.end).format('DD.MM.YYYY HH:mm'),
        reason: getAbsenceReasonTranslation(a.reason),
        comment: a.comment ?? '',
      };
    }) ?? [];

  const columns: TableColumns<TeacherAbsenceTableType>[] = [
    {
      header: t('persons.titleNeutral'),
      id: 'teacherName',
      accessorKey: 'teacherName',
      size: 250,
    },
    {
      header: t('common.start'),
      id: 'start',
      accessorKey: 'start',
    },
    {
      header: t('common.end'),
      id: 'end',
      accessorKey: 'end',
    },
    {
      header: t('absences.reason'),
      id: 'reason',
      accessorKey: 'reason',
      size: 150,
    },
    {
      header: t('common.comment'),
      id: 'comment',
      accessorKey: 'comment',
      size: 250,
    },
  ];

  return (
    <Table<TeacherAbsenceTableType>
      columns={columns}
      data={absences}
      showShadow
      showBorderRadius
      loading={loading}
      showActionBar
      actionBarSettings={{ showAddButton: true, showExpertFilter: true }}
      onAddClick={onAdd}
      isOnWhite={false}
      lastColWidth='80px'
      lastCol={(row) => {
        return (
          <ButtonGroup>
            <Button
              hierarchy='secondary'
              onClick={() => onEdit(row.original.uuid)}
              icon={<EditIcon className='small' />}
            />
            <Button
              hierarchy='secondary'
              onClick={() => onDelete(row.original.uuid)}
              icon={<DeleteIcon className='small' color='var(--color-error)' />}
            />
          </ButtonGroup>
        );
      }}
    />
  );
});
